import {useEffect, useState} from 'react';

/**
 * An <img> that degrades instead of showing a broken-image glyph.
 *
 * Many listing images are hot-linked from third-party sites (e.g.
 * studyinturkeyvs.yok.gov.tr), which regularly fail with 404s or
 * ERR_HTTP2_PROTOCOL_ERROR. A plain <img> renders the browser's broken-image
 * icon in that case, which looks worse than showing nothing.
 *
 * Pass an ordered list of candidate `sources` (e.g. cover → logo): each is
 * tried in turn, and when all of them fail the `fallback` node renders.
 */
export function SafeImage({
  sources,
  alt = '',
  className,
  fallback = null,
  onResolved,
}: {
  sources: (string | null | undefined)[];
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
  /** Called with the index of the source that ended up rendering. */
  onResolved?: (index: number) => void;
}) {
  const candidates = sources.filter(
    (s): s is string => typeof s === 'string' && s.trim() !== '',
  );
  const [index, setIndex] = useState(0);

  // Reset when the candidate list changes (e.g. navigating between listings),
  // otherwise a previously-failed index would suppress a valid new image.
  useEffect(() => {
    setIndex(0);
  }, [candidates.join('|')]);

  if (!candidates.length || index >= candidates.length) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={candidates[index]}
      alt={alt}
      className={className}
      loading="lazy"
      onLoad={() => onResolved?.(index)}
      // Advance to the next candidate; once past the end the fallback renders.
      onError={() => setIndex(i => i + 1)}
    />
  );
}
